import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Department } from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import useSearchParam from '@lib/hooks/useSearchParam';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { APP_URLS } from 'constants/urls';
import { UseTeamQueriesResult } from 'routes/Teams/utils/useTeamQueries';
import getTeamDepartmentsTableColumns from '../utils/getTeamDepartmentsTableColumns';
import { UseTeamActionsResult } from '../utils/useTeamActions';
import { TeamViewMode } from '../enums';

type Props = {
  useTeamActionsResult: UseTeamActionsResult;
  useTeamQueriesResult: UseTeamQueriesResult;
  isViewOnly: boolean;
};

export default function TeamViewDepartments({
  useTeamActionsResult,
  useTeamQueriesResult,
  isViewOnly,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery] = useSearchParam();

  const { useTeamQueryHookResult, useTeamDepartmentsQueryHookResult } =
    useTeamQueriesResult;
  const { data } = useTeamQueryHookResult;
  const { name, readOnly } = data || {};
  const isUpdateAvailable = !readOnly;

  const { onViewChange } = useTeamActionsResult;

  const { collection, loading, firstLoading, error, totalCount } =
    useTeamDepartmentsQueryHookResult;

  const onEditTeam = () => onViewChange(TeamViewMode.Edit);

  const onView = (department: Department) => {
    if (department?.id) {
      navigate(
        APP_URLS.dashboard.departments.view.getDynamicPath({
          pathParts: { id: department.id },
        }),
      );
    }
  };

  const tableColumns = getTeamDepartmentsTableColumns({ onView, t });

  const isEmptyStateVisible = collection.length === 0 && !error && !loading;
  const isEmptyStateByOutOfRangePage = isEmptyStateVisible && !!totalCount;
  let emptyState: React.ReactNode = null;
  if (isEmptyStateByOutOfRangePage) {
    emptyState = <PageOutOfRangeEmptyState totalEntries={totalCount} />;
  } else if (isEmptyStateVisible) {
    emptyState = (
      <ListEmptyState
        title={t('no-team-departments-linked-yet')}
        text={t('no-team-departments-linked-text')}
        actions={
          !isUpdateAvailable || isViewOnly
            ? []
            : [
                <Button
                  key="edit"
                  buttonText={t('edit-team')}
                  buttonType={ButtonTypes.primaryFilled}
                  leftIcon="edit"
                  onClick={onEditTeam}
                  disabled={loading}
                />,
              ]
        }
      />
    );
  }

  const isErrorStateVisible = !!error && !loading;
  const errorState = isErrorStateVisible ? (
    <ErrorPage
      status={getStatusFromError(error)}
      description={getDescriptionFromError(error)}
    />
  ) : null;

  const isContentVisible = !error && !firstLoading && collection?.length;
  const content = isContentVisible ? (
    <>
      {(!!collection?.length || searchQuery) && (
        <Search placeholder={t('departments-search-placeholder')} />
      )}
      <Table
        columns={tableColumns}
        data={collection}
        emptyState={emptyState}
        id="teamDepartments"
        loading={loading}
        totalEntries={totalCount}
      />
    </>
  ) : null;

  return (
    <WhiteBox>
      <DashboardTitleHeader
        title={name}
        actions={
          !isUpdateAvailable || isViewOnly ? (
            <ViewOnlyChip key="view" />
          ) : (
            <Button
              key="edit"
              buttonText={t('edit')}
              buttonType={ButtonTypes.primaryOutlined}
              leftIcon="edit"
              onClick={onEditTeam}
            />
          )
        }
      />
      {firstLoading && <CircleLoader />}
      {errorState}
      {emptyState}
      {content}
    </WhiteBox>
  );
}
